import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function PropertyManagerLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/property-manager/login`, formData, {
        withCredentials: true
      });

      if (response.data.success) {
        // Store session_token in localStorage as fallback for iOS Safari
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success("Login successful!");
        navigate("/crm");  // Both admin and property managers go to /crm
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Header variant="full" />

      <div className="container mx-auto px-6 py-20">
        <div className="max-w-md mx-auto">
          <Card className="bg-white" style={{ border: '1px solid #F7F7F7', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)', borderRadius: '12px' }}>
            <CardHeader>
              <CardTitle className="text-2xl text-center" style={{ color: '#1E1E1E' }}>
                Property Manager Login
              </CardTitle>
              <p className="text-center text-sm mt-2" style={{ color: '#3A3A3A' }}>
                Access your leads dashboard
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="border-slate-300"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full text-white font-semibold transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Logging in..." : "Login"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm transition-colors duration-300"
                  style={{ color: '#3A3A3A' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#3A3A3A'}
                >
                  Forgot your password?
                </button>
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm" style={{ color: '#3A3A3A' }}>
                  Don't have an account?{" "}
                  <button
                    onClick={() => navigate("/company-signup")}
                    className="font-medium transition-colors duration-300"
                    style={{ color: '#3b82f6' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                  >
                    Sign up for leads
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
